/**
 * <ev-jump-nav>: the jump nav's behavior (sections/ev-jump-nav.liquid, Our Science). Moved
 * unchanged from the section's inline script (ruling R115a).
 *
 * Markup contract (Liquid renders it):
 *   <div class="ev-jump-nav-section">                the section wrapper, sticky under the header
 *     <ev-jump-nav class="ev-jump-nav-host">
 *       <nav class="ev-jump-nav" aria-label="On this page">
 *         <ul role="list"><li><a href="#target">Chip</a></li>...</ul>
 *       </nav>
 *     </ev-jump-nav>
 *   </div>
 *
 * - No dead anchors: a chip shows only while its target id is on the page exactly once; with
 *   no chip left the nav is hidden.
 * - While the bar is stuck, the active scroller (html below 990px, .page-wrapper at 990px and
 *   up) gets scroll-padding-top of header plus nav, so targets and focused elements land below
 *   the bar (2.4.11). A chip click sets it before the jump and holds it for 1.5s, because the
 *   bar sticks on the way to the target. Otherwise the padding is the header's alone
 *   (assets/ev-chrome.css).
 * - While the bar is sticky but not stuck, each chip target gets a scroll-margin-top of the
 *   bar's height (QA N5), so a link from above the bar lands below it once it sticks; while
 *   stuck the margin is cleared, never counted twice.
 * - A deep link (a #hash on load whose target comes after the bar) gets the padding and is
 *   scrolled into view once.
 * Removed from the page (theme editor), it drops its listeners, timers, padding and margins.
 */
class EvJumpNav extends HTMLElement {
  /** @type {HTMLElement | null} */
  #nav = null;
  /** @type {HTMLElement | null} */
  #wrapper = null;
  /** @type {HTMLElement | null} */
  #padded = null;
  #wide = window.matchMedia('(min-width: 990px)');
  #jumping = 0;
  #frame = 0;

  connectedCallback() {
    if (this.#nav) return;
    const nav = /** @type {HTMLElement | null} */ (this.querySelector('.ev-jump-nav'));
    if (!nav) return;
    this.#nav = nav;
    this.#wrapper = /** @type {HTMLElement} */ (nav.closest('.ev-jump-nav-section') || nav);

    nav.addEventListener('click', this.#onClick);
    document.addEventListener('scroll', this.#schedule, { capture: true, passive: true });
    window.addEventListener('resize', this.#schedule);
    this.#wide.addEventListener('change', this.#schedule);

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.#start, { once: true });
    else this.#start();
  }

  disconnectedCallback() {
    const nav = this.#nav;
    if (!nav) return;
    nav.removeEventListener('click', this.#onClick);
    document.removeEventListener('scroll', this.#schedule, { capture: true });
    window.removeEventListener('resize', this.#schedule);
    this.#wide.removeEventListener('change', this.#schedule);
    document.removeEventListener('DOMContentLoaded', this.#start);
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#jumping);
    this.#frame = 0;
    this.#jumping = 0;
    this.#padded?.style.removeProperty('scroll-padding-top');
    this.#padded = null;
    this.#margin(false);
    this.#nav = null;
    this.#wrapper = null;
  }

  #start = () => {
    this.#guard();
    this.#update();
    this.#landHash();
  };

  /** The page's scroll container: .page-wrapper at 990px and wider (Horizon), the root below. */
  #scroller() {
    return (this.#wide.matches && document.querySelector('.page-wrapper')) || document.documentElement;
  }

  /** No dead anchors: a chip shows only while its target is on the page exactly once. */
  #guard() {
    const nav = this.#nav;
    if (!nav) return;
    for (const link of nav.querySelectorAll('a[href^="#"]')) {
      const id = decodeURIComponent(link.getAttribute('href').slice(1));
      link.closest('li').hidden = !id || document.querySelectorAll(`[id="${CSS.escape(id)}"]`).length !== 1;
    }
    nav.hidden = !nav.querySelector('li:not([hidden])');
  }

  #sticky() {
    return Boolean(this.#wrapper) && getComputedStyle(this.#wrapper).position === 'sticky';
  }

  #stuck() {
    if (!this.#sticky()) return false;
    const top = parseFloat(getComputedStyle(this.#wrapper).top) || 0;
    return Math.abs(this.#wrapper.getBoundingClientRect().top - top) < 1;
  }

  /** The chips' targets, each on the page exactly once (the guard hides the others). */
  #chipTargets() {
    const found = [];
    if (!this.#nav) return found;
    for (const link of this.#nav.querySelectorAll('li:not([hidden]) a[href^="#"]')) {
      let id = '';
      try {
        id = decodeURIComponent(link.getAttribute('href').slice(1));
      } catch {
        continue;
      }
      const target = id && document.getElementById(id);
      if (target) found.push(target);
    }
    return found;
  }

  /**
   * A scroll margin of the bar's height on every chip target while the bar is not stuck, so a
   * jump from above the bar clears it once it sticks; none while stuck.
   * @param {boolean} on
   */
  #margin(on) {
    const height = this.#nav ? Math.ceil(this.#nav.getBoundingClientRect().height) : 0;
    for (const target of this.#chipTargets()) {
      if (on && height > 0) target.style.setProperty('scroll-margin-top', `${height}px`);
      else target.style.removeProperty('scroll-margin-top');
    }
  }

  /** @param {boolean} on */
  #pad(on) {
    const target = this.#scroller();
    if (this.#padded && this.#padded !== target) this.#padded.style.removeProperty('scroll-padding-top');
    if (on) {
      const height = Math.ceil(this.#nav.getBoundingClientRect().height);
      target.style.setProperty('scroll-padding-top', `calc(var(--ev-header-height) + ${height}px)`);
      this.#padded = target;
    } else {
      target.style.removeProperty('scroll-padding-top');
      this.#padded = null;
    }
    this.#margin(!on && this.#sticky());
  }

  /** While a chip's jump is in flight the bar sticks on the way; keep the padding. */
  #update = () => {
    this.#frame = 0;
    if (!this.#nav) return;
    const on = this.#stuck();
    if (!on && this.#jumping) return;
    this.#pad(on);
  };

  #schedule = () => {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#update);
  };

  #hold() {
    this.#pad(true);
    clearTimeout(this.#jumping);
    this.#jumping = setTimeout(() => {
      this.#jumping = 0;
      this.#schedule();
    }, 1500);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('a[href^="#"]') || !this.#sticky()) return;
    this.#hold();
  };

  /** A deep link to a target after the bar: pad first, then bring it into view once. */
  #landHash() {
    let id = '';
    try {
      id = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return;
    }
    if (!id || !this.#sticky()) return;
    const targets = document.querySelectorAll(`[id="${CSS.escape(id)}"]`);
    if (targets.length !== 1) return;
    const target = targets[0];
    if (!(this.#wrapper.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
    this.#hold();
    target.scrollIntoView({ block: 'start', behavior: 'instant' });
  }
}

if (!customElements.get('ev-jump-nav')) customElements.define('ev-jump-nav', EvJumpNav);
